export const reviewFlagTypes = [
  "missing_required_field",
  "low_confidence",
  "amount_mismatch",
  "date_out_of_range",
  "duplicate_record",
  "unsupported_layout",
  "provider_warning",
  "policy_violation",
] as const

export type ReviewFlagType = (typeof reviewFlagTypes)[number]

export const reviewFlagSeverities = ["info", "warning", "error"] as const

export type ReviewFlagSeverity = (typeof reviewFlagSeverities)[number]

export const reviewFlagScopes = [
  "document",
  "summary_field",
  "record",
  "source_grounding",
  "export",
  "posting",
] as const

export type ReviewFlagScope = (typeof reviewFlagScopes)[number]

export const reviewDecisionActions = [
  "confirm",
  "correct",
  "dismiss",
  "reject",
] as const

export type ReviewDecisionAction = (typeof reviewDecisionActions)[number]

/**
 * No "qfx". Quicken validates a QFX file's `<INTU.BID>` against Intuit's
 * directory of licensed institutions and refuses any file whose id is not on
 * it, with "unable to verify the financial institution information for this
 * download". LedgerBox holds no such licence, so a QFX export would be a paid
 * download that Quicken rejects.
 *
 * The OFX writer takes the Intuit tags as an option for exactly this reason
 * (see `buildOfx` in apps/api). Adding "qfx" here, one template below, and one
 * caller is the whole job on the day a BID exists.
 */
export const exportFormats = [
  "csv",
  "json",
  "xlsx",
  "qbo",
  "ofx",
  "qif",
] as const

export type ExportFormat = (typeof exportFormats)[number]

export const exportTemplates = [
  "ledgerbox_transactions_csv",
  "ledgerbox_transactions_xlsx",
  "ledgerbox_normalized_json",
  // Generic rows-to-CSV templates for the non-bank types (line items, table rows).
  "csv_line_items",
  "csv_table",
  // Every extracted row of every record type, for types that produce more than
  // one (a merchant statement's fee lines and card volumes).
  "csv_rows",
  // One row per document, columns from the type's summaryFields. The export
  // shape for summary-only types (W-2, 1099, check).
  "csv_summary",
  // Realized gain rows in Schedule D column order, for brokerage statements.
  "realized_gains_csv",
  "quickbooks_csv_3_column",
  "quickbooks_csv_4_column",
  "quickbooks_web_connect_qbo",
  // Open Financial Exchange 1.02, the same document the QBO template writes
  // without Intuit's extensions. Read by Quicken, Sage, MoneyDance, GnuCash,
  // and most desktop accounting software with a bank-import path.
  "ofx_bank_statement",
  // Quicken Interchange Format. Older and simpler than OFX: no account header
  // and no statement period, so it merges across documents where QBO cannot.
  "qif_bank_statement",
] as const

export type ExportTemplate = (typeof exportTemplates)[number]

// QuickBooks/accounting export templates — gated to paid subscriptions (D12).
// The LedgerBox CSV/JSON templates stay ungated.
export const accountingExportTemplates = [
  "quickbooks_csv_3_column",
  "quickbooks_csv_4_column",
  "quickbooks_web_connect_qbo",
  // Gated with the QuickBooks files rather than with the LedgerBox CSV and
  // JSON, because they are the same thing: a file shaped for someone else's
  // accounting software to import. The trial exports rows; a paid plan exports
  // the file another product asked for.
  "ofx_bank_statement",
  "qif_bank_statement",
] as const satisfies readonly ExportTemplate[]

export function isAccountingExportTemplate(
  template: string | null | undefined
): boolean {
  return (
    template != null && new Set<string>(accountingExportTemplates).has(template)
  )
}

export const postingDestinations = ["quickbooks", "xero", "api"] as const

export type PostingDestination = (typeof postingDestinations)[number]

export const postingAttemptStates = [
  "queued",
  "running",
  "succeeded",
  "failed",
  "canceled",
] as const

export type PostingAttemptState = (typeof postingAttemptStates)[number]

export const postingErrorClassifications = [
  "validation",
  "auth",
  "rate_limit",
  "destination",
  "network",
  "unknown",
] as const

export type PostingErrorClassification =
  (typeof postingErrorClassifications)[number]

export const usageLedgerEntryTypes = [
  "processing_charge",
  "credit_grant",
  "credit_adjustment",
  "refund",
  "usage_reversal",
] as const

export type UsageLedgerEntryType = (typeof usageLedgerEntryTypes)[number]

export const usageLedgerUnitTypes = [
  "document",
  "page",
  "run",
  "credit",
  "usd_cent",
] as const

export type UsageLedgerUnitType = (typeof usageLedgerUnitTypes)[number]

export const integrationProviders = [
  "quickbooks",
  "xero",
  "webhook",
  "api",
  "gmail",
  "outlook",
  "google_drive",
] as const

export type IntegrationProvider = (typeof integrationProviders)[number]

export const integrationConnectionTypes = [
  "accounting",
  "webhook",
  "api",
] as const

export type IntegrationConnectionType =
  (typeof integrationConnectionTypes)[number]

export const domainEventTypes = [
  "document_processed",
  "flags_created",
  "document_approved",
  "export_generated",
  "posting_completed",
  "batch_completed",
  "artifact_expired",
  "purge_completed",
] as const

export type DomainEventType = (typeof domainEventTypes)[number]

export const outboxMessageKinds = [
  "webhook",
  "queue",
  "email",
  "analytics",
  "notification",
  // Workflow runs. Added in phase six; the enum value needs migration 0020,
  // because outbox_message_kind is a Postgres enum and not a check constraint.
  "workflow",
  // Agent evaluations. The enum value needs migration 0041, the same way the
  // workflow kind needed 0020.
  "agent",
] as const

export type OutboxMessageKind = (typeof outboxMessageKinds)[number]

export const outboxMessageStatuses = [
  "pending",
  "processing",
  "sent",
  "retry",
  "dead_lettered",
  "canceled",
] as const

export type OutboxMessageStatus = (typeof outboxMessageStatuses)[number]

export const actorTypes = ["user", "api_key", "system"] as const

export type ActorType = (typeof actorTypes)[number]

export const apiEnvironmentKeys = ["local", "dev", "prod", "test"] as const

export type ApiEnvironmentKey = (typeof apiEnvironmentKeys)[number]

export const documentDerivedStates = [
  "processing",
  "needs_review",
  "ready",
  "approved",
  "posted",
  "failed",
  "expired",
  "deleted",
  "needs_credits",
] as const

export type DocumentDerivedState = (typeof documentDerivedStates)[number]

// Ordering options for the documents list. `updated_desc` (most recent first) is
// the default. Single source of truth shared by the API schemas, the api-client,
// the db repository, and the dashboard sort control. Records/period orderings
// sort on the extraction summary (documents without one sort last).
export const documentSortKeys = [
  "updated_desc",
  "updated_asc",
  "title_asc",
  "title_desc",
  "records_desc",
  "records_asc",
  "period_desc",
  "period_asc",
] as const

export type DocumentSortKey = (typeof documentSortKeys)[number]

/**
 * Whether a stored link is somewhere inside LedgerBox.
 *
 * Notifications carry a link_path written by whatever produced the notice, and
 * the bell renders it as a navigation target. An absolute URL passes straight
 * through a router as an off-site href, which turns a notice into a way to
 * send somebody somewhere else, so a producer's string is checked before it is
 * ever treated as a destination.
 *
 * One leading slash, and not two: "//evil.com" is protocol-relative and leaves
 * the site just as surely as "https://" does.
 */
export const isInternalLinkPath = (value: string | null): value is string =>
  value !== null &&
  value.startsWith("/") &&
  !value.startsWith("//") &&
  !value.includes("\\")
